import {newUUID} from '../utils/uuid.ts';
import {DefaultDiscoveryServiceFactory, type DiscoveryService, type DiscoveryServiceSettings} from '../discovery/index.ts';
import {SimpleDiscoveryNode} from '../discovery/SimpleDiscoveryNode.ts';
import {type Member} from '../cluster/Member.ts';
import {type MemberVersion, parseVersion} from '../version/MemberVersion.ts';
import {type DiscoveryConfig} from '../config/DiscoveryConfig.ts';
import info from '../../package.json' with {type: 'json'};
import {type AddressPicker, DefaultAddressPicker} from './AddressPicker.ts';
import getLogger, {type Logger} from '../logging.ts';
import gatewayServer, {GatewayServer} from '@interopio/gateway-server';
import {Config} from '../config/Config.ts';
import {BridgeLicenseValidator} from "../license/BridgeLicenseValidator.ts";
import { type LicenseValidator} from "../license/LicenseValidator.ts";
import {userInfo} from 'node:os';
import {mesh} from '../../../bridge-mesh/src/index.ts';

function logStartingInfo(logger: Logger) {
    logger.info(`Starting io.Bridge v${info.version} using Node.js ${process.version} with PID ${process.pid} (started by ${userInfo().username} in ${process.cwd()})`);
}

function createDiscoveryService(logger: Logger,
                                conf: DiscoveryConfig,
                                isAutoDetectionEnabled: boolean,
                                localMember: Promise<Member>): DiscoveryService {
    const factory = new DefaultDiscoveryServiceFactory();

    const node = localMember.then(member => new SimpleDiscoveryNode(member.address)) ;

    const settings: DiscoveryServiceSettings = {
        mode: 'member',
        node: node,
        conf: conf,
        auto: isAutoDetectionEnabled,
        logger: logger,
    };
    return factory.newDiscoveryService(settings);
}

function createAddressPicker(node: BridgeNode): AddressPicker {
    const config = node.config;
    const logger = node.getLogger('AddressPicker');
    return new DefaultAddressPicker(config, logger);
}

export class BridgeNode {
    readonly config: Config;
    private readonly uuid: string;
    private readonly discoveryService: DiscoveryService;
    private readonly version: MemberVersion;
    private server?: GatewayServer.Server;
    private readonly licenseValidator: LicenseValidator;

    constructor(config: Config) {
        this.config = config;

        this.licenseValidator = BridgeLicenseValidator(this.getLogger('license-validator'));
        this.licenseValidator.validate(config.license);

        this.uuid = newUUID();
        this.version = parseVersion(info.version);
        const addressPicker = createAddressPicker(this);


        const localMemberPromise: Promise<Member> = addressPicker.pickAddress()
            .then(() => addressPicker.getPublicAddressFor('member'))
            .then((publicAddress) => {
                return {
                    local: true,
                    address: publicAddress,
                    uuid: this.uuid,
                    version: this.version,
                };
            });

        const joinConfig = config.network.join;

        const isAutoDetectionEnabled = joinConfig.autoDetectionEnabled;

        const discoveryConfig: DiscoveryConfig = joinConfig.discovery;

        this.discoveryService = createDiscoveryService(getLogger("discovery-service"), discoveryConfig, isAutoDetectionEnabled, localMemberPromise);
    }

    getLogger(name: string) {
        return getLogger(name);
    }

    private async scheduleLicenseCheck() {
        const task = async () => {
            try {
                this.licenseValidator.validate(this.config.license, {
                    logSuccessMessages: false,
                });
            }
            catch (error) {
                // this.logger.error(error);
                await this.stop();
            }
        };

        const licenseCheckInterval = 1000 * 60 * 60 * 24; // 1 day
        return setInterval(task, licenseCheckInterval);
    }

    async start() {
        const config: GatewayServer.ServerConfig =  {
            port: this.config.server.port,
            host: this.config.server.host,
            app: async (configurer, config) => {
                await mesh({
                    logger: this.getLogger('mesh'),
                    enabled: true,
                    timeout: this.config.mesh.timeout ?? 60000, // 60 seconds
                    socket: {
                        ping: this.config.server.wsPingInterval ?? 30000, // 30 seconds
                    }
                }, configurer, config);
            },
            auth: {type: 'none', ...this.config.server.auth},
            cors: this.config.server.cors
        };
        if (this.config.gateway.enabled) {
            config.gateway = {
                clients: {
                    inactive_seconds: 0,
                },
                ping: this.config.server.wsPingInterval ?? undefined,
                mesh: {
                    cluster: {
                        endpoint: `http://localhost:${this.config.server.port}`,
                        directory: {interval: 1000 * 30}, // 30 seconds
                    }
                },
                contexts: {
                    lifetime: this.config.gateway.contexts.lifetime,
                    visibility: [{restrictions: 'cluster'}]
                },
                peers: {
                    visibility: [{
                        domain: 'interop',
                        restrictions: 'local'
                    }]
                }
            }
        }
        logStartingInfo(this.getLogger('node'));
        this.server = await gatewayServer(config);
        if (false) {
            await this.discoveryService.start();
        }
        await this.scheduleLicenseCheck();
    }

    async stop() {
        if (this.server)
        {
            await this.server.close()
        }
        try {
            await this.discoveryService.close();
        } catch (e) {
            // ignore
        }
    }
}
